/**
 * Thin client for the Noti API. Every call goes to `/api/*`, which Vite proxies
 * in development and Caddy routes in production — the server itself mounts its
 * routes at the root.
 */

import type {
  CreateTokenInput,
  CreateWebhookInput,
  FileView,
  GeneralAccessInput,
  InboxKind,
  PageAccessLevel,
  Property,
  TokenView,
  UpdateWorkspaceInput,
  View,
  ViewSettings,
  WebhookView,
  WorkspaceSettings,
} from '@noti/shared';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function parseError(response: Response): Promise<ApiError> {
  const body = (await response.json().catch(() => null)) as {
    error?: { code?: string; message?: string };
  } | null;
  return new ApiError(
    response.status,
    body?.error?.code ?? 'unknown',
    body?.error?.message ?? 'Request failed',
  );
}

/** Request a sign-in link. Always resolves: the API never confirms an address. */
export async function requestMagicLink(email: string): Promise<void> {
  const response = await fetch('/api/auth/magic-link', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!response.ok && response.status !== 202) throw await parseError(response);
}

/** Send the browser to Google. The API sets the state + PKCE cookie and redirects. */
export function startGoogleLogin(): void {
  window.location.assign('/api/auth/google');
}

export interface CurrentUser {
  id: string;
  email: string;
  name: string | null;
  locale: string;
}

export async function fetchCurrentUser(): Promise<CurrentUser | null> {
  const response = await fetch('/api/auth/me', { credentials: 'same-origin' });
  if (response.status === 401) return null;
  if (!response.ok) throw await parseError(response);
  const body = (await response.json()) as { user: CurrentUser };
  return body.user;
}

/** Log out. Sends the CSRF token the API set alongside the session cookie. */
export async function logout(): Promise<void> {
  const csrf = readCookie('noti_csrf');
  const response = await fetch('/api/auth/logout', {
    method: 'POST',
    credentials: 'same-origin',
    headers: csrf ? { 'x-csrf-token': csrf } : {},
  });
  if (!response.ok && response.status !== 204) throw await parseError(response);
}

/** Set the display name. Required once: a magic link sign-in has none. */
export async function updateProfileName(name: string): Promise<CurrentUser> {
  const csrf = readCookie('noti_csrf');
  const response = await fetch('/api/auth/profile', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
      ...(csrf ? { 'x-csrf-token': csrf } : {}),
    },
    body: JSON.stringify({ name }),
  });
  if (!response.ok) throw await parseError(response);
  const body = (await response.json()) as { user: CurrentUser };
  return body.user;
}

export interface WorkspaceSummary {
  id: string;
  name: string;
  slug: string;
  icon: string | null;
  role: string;
  memberCount: number;
  settings: WorkspaceSettings;
}

export interface PageSummary {
  id: string;
  parentId: string | null;
  /** For a database row: the page the database sits on. */
  parentPageId: string | null;
  title: string;
  icon: string | null;
  cover: string | null;
  position: string;
  trashedAt: string | null;
  favorite: boolean;
}

export interface PageDetail extends PageSummary {
  workspaceId: string;
  isLocked: boolean;
  font: 0 | 1 | 2;
  small: boolean;
  wide: boolean;
  document: { version: number; content: Record<string, unknown> };
}

/** Somebody who opened a page, and when they did so last. */
export interface PageViewer {
  userId: string;
  name: string;
  viewedAt: string;
}

async function send(path: string, method: string, body?: unknown): Promise<Response> {
  const csrf = readCookie('noti_csrf');
  return fetch(path, {
    method,
    credentials: 'same-origin',
    headers: {
      ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
      ...(csrf ? { 'x-csrf-token': csrf } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

export async function fetchWorkspaces(): Promise<WorkspaceSummary[]> {
  const response = await fetch('/api/workspaces', { credentials: 'same-origin' });
  if (!response.ok) throw await parseError(response);
  const body = (await response.json()) as { workspaces: WorkspaceSummary[] };
  return body.workspaces;
}

export async function fetchPages(workspaceId: string): Promise<PageSummary[]> {
  const response = await fetch(`/api/workspaces/${workspaceId}/pages`, {
    credentials: 'same-origin',
  });
  if (!response.ok) throw await parseError(response);
  const body = (await response.json()) as { pages: PageSummary[] };
  return body.pages;
}

export async function fetchTrash(workspaceId: string): Promise<PageSummary[]> {
  const response = await fetch(`/api/workspaces/${workspaceId}/trash`, {
    credentials: 'same-origin',
  });
  if (!response.ok) throw await parseError(response);
  const body = (await response.json()) as { pages: PageSummary[] };
  return body.pages;
}

export async function searchPages(workspaceId: string, query: string): Promise<PageSummary[]> {
  const response = await fetch(
    `/api/workspaces/${workspaceId}/search?q=${encodeURIComponent(query)}`,
    { credentials: 'same-origin' },
  );
  if (!response.ok) throw await parseError(response);
  const body = (await response.json()) as { pages: PageSummary[] };
  return body.pages;
}

export async function createPage(
  workspaceId: string,
  input: { title?: string; parentId?: string | null },
): Promise<PageSummary> {
  const response = await send(`/api/workspaces/${workspaceId}/pages`, 'POST', input);
  if (!response.ok) throw await parseError(response);
  const body = (await response.json()) as { page: PageSummary };
  return body.page;
}

export async function fetchPage(pageId: string): Promise<PageDetail> {
  const response = await fetch(`/api/pages/${pageId}`, { credentials: 'same-origin' });
  if (!response.ok) throw await parseError(response);
  const body = (await response.json()) as { page: PageDetail };
  return body.page;
}

export async function updatePage(
  pageId: string,
  patch: {
    title?: string;
    icon?: string | null;
    cover?: string | null;
    parentId?: string | null;
    beforeId?: string | null;
    isLocked?: boolean;
    font?: 0 | 1 | 2;
    small?: boolean;
    wide?: boolean;
  },
): Promise<PageSummary> {
  const response = await send(`/api/pages/${pageId}`, 'PATCH', patch);
  if (!response.ok) throw await parseError(response);
  const body = (await response.json()) as { page: PageSummary };
  return body.page;
}

export async function saveDocument(
  pageId: string,
  version: number,
  content: Record<string, unknown>,
): Promise<{ version: number; content: Record<string, unknown> }> {
  const response = await send(`/api/pages/${pageId}/document`, 'PUT', { version, content });
  if (!response.ok) throw await parseError(response);
  const body = (await response.json()) as {
    document: { version: number; content: Record<string, unknown> };
  };
  return body.document;
}

/** Pin or unpin a page in the personal favorites list (productspec §5). */
export async function setFavorite(pageId: string, favorite: boolean): Promise<void> {
  const response = await send(`/api/pages/${pageId}/favorite`, 'PUT', { favorite });
  if (!response.ok) throw await parseError(response);
}

export async function trashPage(pageId: string): Promise<void> {
  const response = await send(`/api/pages/${pageId}/trash`, 'POST');
  if (!response.ok) throw await parseError(response);
}

export async function restorePage(pageId: string): Promise<void> {
  const response = await send(`/api/pages/${pageId}/restore`, 'POST');
  if (!response.ok) throw await parseError(response);
}

/** Permanently remove a page (and its subtree) from Trash. */
export async function deletePage(pageId: string): Promise<void> {
  const response = await send(`/api/pages/${pageId}`, 'DELETE');
  if (!response.ok && response.status !== 204) throw await parseError(response);
}

/** Settings → General: rename the workspace or flip one of its switches. */
export async function updateWorkspace(
  workspaceId: string,
  input: UpdateWorkspaceInput,
): Promise<{ name: string; slug: string; settings: WorkspaceSettings }> {
  const response = await send(`/api/workspaces/${workspaceId}`, 'PATCH', input);
  if (!response.ok) throw await parseError(response);
  const body = (await response.json()) as {
    workspace: { name: string; slug: string; settings: WorkspaceSettings };
  };
  return body.workspace;
}

export async function inviteToWorkspace(
  workspaceId: string,
  email: string,
  role: 'admin' | 'member' | 'guest',
): Promise<void> {
  const response = await send(`/api/workspaces/${workspaceId}/invites`, 'POST', { email, role });
  if (!response.ok) throw await parseError(response);
}

export interface SharePerson {
  principalType: 'user' | 'email';
  principalId: string;
  name: string;
  email: string;
  level: PageAccessLevel | null;
}

export interface PageSharing {
  people: SharePerson[];
  general: GeneralAccessInput['general'];
  inheritedFrom: string | null;
  workspaceName: string;
  memberCount: number;
  canEdit: boolean;
}

export async function fetchPageSharing(pageId: string): Promise<PageSharing> {
  const response = await send(`/api/pages/${pageId}/shares`, 'GET');
  if (!response.ok) throw await parseError(response);
  const body = (await response.json()) as { sharing: PageSharing };
  return body.sharing;
}

export async function setPageShare(
  pageId: string,
  principal: { type: 'user' | 'email'; id: string },
  level: PageAccessLevel,
): Promise<void> {
  const response = await send(`/api/pages/${pageId}/shares`, 'PUT', { principal, level });
  if (!response.ok) throw await parseError(response);
}

export async function removePageShare(
  pageId: string,
  principal: { type: 'user' | 'email'; id: string },
): Promise<void> {
  const response = await send(`/api/pages/${pageId}/shares`, 'DELETE', principal);
  if (!response.ok && response.status !== 204) throw await parseError(response);
}

export async function setPageGeneralAccess(
  pageId: string,
  general: GeneralAccessInput['general'],
): Promise<void> {
  const response = await send(`/api/pages/${pageId}/share-access`, 'PUT', { general });
  if (!response.ok) throw await parseError(response);
}

export interface InboxItem {
  id: string;
  kind: InboxKind;
  actorName: string | null;
  pageId: string | null;
  pageTitle: string | null;
  excerpt: string | null;
  read: boolean;
  archived: boolean;
  createdAt: string;
}

export interface InboxList {
  items: InboxItem[];
  unread: number;
}

export async function fetchInbox(filter: {
  view?: 'all' | 'unread' | 'archived';
  mentionsOnly?: boolean;
  workspaceId?: string;
}): Promise<InboxList> {
  const query = new URLSearchParams();
  if (filter.view) query.set('view', filter.view);
  if (filter.mentionsOnly) query.set('mentionsOnly', 'true');
  if (filter.workspaceId) query.set('workspaceId', filter.workspaceId);
  const response = await send(`/api/inbox?${query.toString()}`, 'GET');
  if (!response.ok) throw await parseError(response);
  return (await response.json()) as InboxList;
}

/** Number of unread items, for the badge next to Inbox in the sidebar. */
export async function fetchUnreadCount(): Promise<number> {
  const response = await send('/api/inbox?view=unread', 'GET');
  if (!response.ok) throw await parseError(response);
  const body = (await response.json()) as InboxList;
  return body.unread;
}

export async function markInboxItemRead(itemId: string): Promise<void> {
  const response = await send(`/api/inbox/${itemId}/read`, 'POST');
  if (!response.ok && response.status !== 204) throw await parseError(response);
}

export async function markAllInboxRead(): Promise<number> {
  const response = await send('/api/inbox/read-all', 'POST');
  if (!response.ok) throw await parseError(response);
  const body = (await response.json()) as { marked: number };
  return body.marked;
}

export async function archiveInboxItem(itemId: string, archived: boolean): Promise<void> {
  const response = await send(`/api/inbox/${itemId}/archive`, 'PUT', { archived });
  if (!response.ok && response.status !== 204) throw await parseError(response);
}

export async function fetchTokens(
  workspaceId: string,
  kind: 'token' | 'connection',
): Promise<TokenView[]> {
  const response = await send(`/api/workspaces/${workspaceId}/tokens`, 'GET');
  if (!response.ok) throw await parseError(response);
  const body = (await response.json()) as { tokens: TokenView[] };
  return body.tokens.filter((token) => token.kind === kind);
}

/** The full secret is in this response only; the server keeps just a hash. */
export async function createToken(
  workspaceId: string,
  input: CreateTokenInput,
): Promise<{ token: TokenView; secret: string }> {
  const response = await send(`/api/workspaces/${workspaceId}/tokens`, 'POST', input);
  if (!response.ok) throw await parseError(response);
  return (await response.json()) as { token: TokenView; secret: string };
}

export async function revokeToken(workspaceId: string, tokenId: string): Promise<void> {
  const response = await send(`/api/workspaces/${workspaceId}/tokens/${tokenId}`, 'DELETE');
  if (!response.ok && response.status !== 204) throw await parseError(response);
}

export async function fetchWebhooks(workspaceId: string): Promise<WebhookView[]> {
  const response = await send(`/api/workspaces/${workspaceId}/webhooks`, 'GET');
  if (!response.ok) throw await parseError(response);
  const body = (await response.json()) as { webhooks: WebhookView[] };
  return body.webhooks;
}

export async function createWebhook(
  workspaceId: string,
  input: CreateWebhookInput,
): Promise<{ webhook: WebhookView; secret: string }> {
  const response = await send(`/api/workspaces/${workspaceId}/webhooks`, 'POST', input);
  if (!response.ok) throw await parseError(response);
  return (await response.json()) as { webhook: WebhookView; secret: string };
}

export async function archiveWebhook(workspaceId: string, webhookId: string): Promise<void> {
  const response = await send(`/api/workspaces/${workspaceId}/webhooks/${webhookId}`, 'DELETE');
  if (!response.ok && response.status !== 204) throw await parseError(response);
}

export interface CommentMessage {
  id: string;
  authorId: string;
  authorName: string;
  text: string;
  edited: boolean;
  createdAt: string;
}

export interface CommentThread {
  /** Discussion id; also the `data-comment-id` of the mark in the document. */
  id: string;
  quote: string | null;
  resolved: boolean;
  createdAt: string;
  messages: CommentMessage[];
}

export async function listComments(pageId: string): Promise<CommentThread[]> {
  const response = await fetch(`/api/pages/${pageId}/comments`, { credentials: 'same-origin' });
  if (!response.ok) throw await parseError(response);
  const body = (await response.json()) as { threads: CommentThread[] };
  return body.threads;
}

export async function createCommentThread(
  pageId: string,
  input: { quote: string | null; text: string },
): Promise<CommentThread> {
  const response = await send(`/api/pages/${pageId}/comments`, 'POST', input);
  if (!response.ok) throw await parseError(response);
  const body = (await response.json()) as { thread: CommentThread };
  return body.thread;
}

export async function replyToCommentThread(
  pageId: string,
  threadId: string,
  text: string,
): Promise<CommentThread> {
  const response = await send(`/api/pages/${pageId}/comments/${threadId}/replies`, 'POST', {
    text,
  });
  if (!response.ok) throw await parseError(response);
  const body = (await response.json()) as { thread: CommentThread };
  return body.thread;
}

/** Upload a file and hang it under a page. */
export async function uploadFile(pageId: string, file: File): Promise<FileView> {
  const csrf = readCookie('noti_csrf');
  const form = new FormData();
  form.append('file', file);
  const response = await fetch(`/api/pages/${pageId}/files`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: csrf ? { 'x-csrf-token': csrf } : {},
    body: form,
  });
  if (!response.ok) throw await parseError(response);
  const body = (await response.json()) as { file: FileView };
  return body.file;
}

/** Remember that this person opened the page. */
export async function recordPageView(pageId: string): Promise<void> {
  const response = await send(`/api/pages/${pageId}/view`, 'POST');
  if (!response.ok) throw await parseError(response);
}

/** Everyone who can see this page, with their last visit. */
export async function fetchPageViewers(pageId: string): Promise<PageViewer[]> {
  const response = await fetch(`/api/pages/${pageId}/viewers`, { credentials: 'same-origin' });
  if (!response.ok) throw await parseError(response);
  const body = (await response.json()) as { viewers: PageViewer[] };
  return body.viewers;
}

/** The pages that point at this page. */
export async function fetchBacklinks(pageId: string): Promise<PageSummary[]> {
  const response = await fetch(`/api/pages/${pageId}/backlinks`, { credentials: 'same-origin' });
  if (!response.ok) throw await parseError(response);
  const body = (await response.json()) as { backlinks: PageSummary[] };
  return body.backlinks;
}

/** The files of a page. */
export async function fetchFiles(pageId: string): Promise<FileView[]> {
  const response = await fetch(`/api/pages/${pageId}/files`, { credentials: 'same-origin' });
  if (!response.ok) throw await parseError(response);
  const body = (await response.json()) as { files: FileView[] };
  return body.files;
}

export async function editComment(pageId: string, commentId: string, text: string): Promise<void> {
  const response = await send(`/api/pages/${pageId}/comments/${commentId}`, 'PATCH', { text });
  if (!response.ok) throw await parseError(response);
}

export async function deleteComment(pageId: string, commentId: string): Promise<void> {
  const response = await send(`/api/pages/${pageId}/comments/${commentId}`, 'DELETE');
  if (!response.ok && response.status !== 204) throw await parseError(response);
}

export async function setCommentThreadResolved(
  pageId: string,
  threadId: string,
  resolved: boolean,
): Promise<void> {
  const response = await send(`/api/pages/${pageId}/comments/${threadId}/resolved`, 'PUT', {
    resolved,
  });
  if (!response.ok) throw await parseError(response);
}

export interface WorkspaceMember {
  id: string;
  name: string;
}

/** Workspace members for the mention picker. */
export async function listWorkspaceMembers(workspaceId: string): Promise<WorkspaceMember[]> {
  const response = await fetch(`/api/workspaces/${workspaceId}/members`, {
    credentials: 'same-origin',
  });
  if (!response.ok) throw await parseError(response);
  const body = (await response.json()) as { members: WorkspaceMember[] };
  return body.members;
}

export interface DatabaseRowSummary {
  id: string;
  title: string;
  icon: string | null;
  color: string | null;
  values: Record<string, string | number | boolean | string[] | null>;
  createdAt: string;
  editedAt: string;
}

export interface DataSourceDetail {
  icon?: string | null;
  relationOptions?: Record<string, { id: string; title: string }[]>;
  id: string;
  pageId: string;
  name: string;
  properties: Property[];
  views: View[];
  viewSettings: ViewSettings | null;
  rows: DatabaseRowSummary[];
}

export async function createDataSource(input: {
  pageId: string;
  name?: string;
}): Promise<DataSourceDetail> {
  const response = await send('/api/data-sources', 'POST', input);
  if (!response.ok) throw await parseError(response);
  const body = (await response.json()) as { dataSource: DataSourceDetail };
  return body.dataSource;
}

export async function fetchDataSource(dataSourceId: string): Promise<DataSourceDetail> {
  const response = await fetch(`/api/data-sources/${dataSourceId}`, {
    credentials: 'same-origin',
  });
  if (!response.ok) throw await parseError(response);
  const body = (await response.json()) as { dataSource: DataSourceDetail };
  return body.dataSource;
}

export async function renameDataSource(dataSourceId: string, name: string): Promise<void> {
  const response = await send(`/api/data-sources/${dataSourceId}`, 'PATCH', { name });
  if (!response.ok) throw await parseError(response);
}

export async function putDataSourceProperties(
  dataSourceId: string,
  properties: Property[],
): Promise<void> {
  const response = await send(`/api/data-sources/${dataSourceId}/properties`, 'PUT', {
    properties,
  });
  if (!response.ok) throw await parseError(response);
}

export async function putDataSourceViews(dataSourceId: string, views: View[]): Promise<void> {
  const response = await send(`/api/data-sources/${dataSourceId}/views`, 'PUT', { views });
  if (!response.ok) throw await parseError(response);
}

/** "Save for me": the user's private settings for every view of this data source. */
export async function putDataSourceViewSettings(
  dataSourceId: string,
  settings: ViewSettings,
): Promise<void> {
  const response = await send(`/api/data-sources/${dataSourceId}/view-settings`, 'PUT', {
    settings,
  });
  if (!response.ok) throw await parseError(response);
}

/** "Back to shared view": drop the user's private settings. */
export async function deleteDataSourceViewSettings(dataSourceId: string): Promise<void> {
  const response = await send(`/api/data-sources/${dataSourceId}/view-settings`, 'DELETE');
  if (!response.ok && response.status !== 204) throw await parseError(response);
}

export async function createDataSourceRow(
  dataSourceId: string,
  input: { values?: Record<string, unknown>; afterId?: string | null },
): Promise<DatabaseRowSummary> {
  const response = await send(`/api/data-sources/${dataSourceId}/rows`, 'POST', input);
  if (!response.ok) throw await parseError(response);
  const body = (await response.json()) as { row: DatabaseRowSummary };
  return body.row;
}

export async function updateDataSourceRow(
  dataSourceId: string,
  rowId: string,
  patch: { propId?: string; value?: unknown; color?: string | null; clear?: boolean },
): Promise<DatabaseRowSummary> {
  const response = await send(`/api/data-sources/${dataSourceId}/rows/${rowId}`, 'PATCH', patch);
  if (!response.ok) throw await parseError(response);
  const body = (await response.json()) as { row: DatabaseRowSummary };
  return body.row;
}

/** Import CSV rows. The client parsed the file; the server matches the names. */
export async function importDataSourceRows(
  dataSourceId: string,
  rows: Record<string, string>[],
): Promise<number> {
  const response = await send(`/api/data-sources/${dataSourceId}/rows/import`, 'POST', { rows });
  if (!response.ok) throw await parseError(response);
  const body = (await response.json()) as { imported: number };
  return body.imported;
}

export async function deleteDataSourceRow(dataSourceId: string, rowId: string): Promise<void> {
  const response = await send(`/api/data-sources/${dataSourceId}/rows/${rowId}`, 'DELETE');
  if (!response.ok && response.status !== 204) throw await parseError(response);
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}
